import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { cancelBooking, listBookings } from '../../api/bookings.js'
import { Card, CardHeader } from '../../components/Card.jsx'
import ErrorBanner from '../../components/ErrorBanner.jsx'
import { firstError, formatDateTime } from '../../utils/format.js'

/**
 * Patient — My bookings.
 *
 * Lists the patient's bookings with three buckets: Upcoming, Past,
 * Cancelled. Upcoming rows have a "Cancel" button that soft-cancels
 * the booking server-side, freeing the slot back into the pool for
 * another patient. The Encounter linked to a cancelled booking is
 * closed (not deleted — preserves audit trail).
 *
 * Doctors see a similar surface from the doctor dashboard via the
 * scheduled encounters list (separate concern); this component is
 * scoped to patient-side viewing.
 */
export default function MyBookings() {
  const qc = useQueryClient()

  const q = useQuery({
    queryKey: ['my-bookings'],
    queryFn: () => listBookings(),
    staleTime: 5_000,
  })

  const cancel = useMutation({
    mutationFn: ({ id, reason }) => cancelBooking(id, reason),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['my-bookings'] }),
  })

  // Bin server rows into upcoming / past / cancelled. The status column
  // already reflects cancelled vs scheduled; we just split scheduled
  // into upcoming-vs-past based on slot_at.
  const now = Date.now()
  const rows = q.data || []
  const upcoming = rows.filter((b) => b.status === 'scheduled' && new Date(b.slot_at).getTime() > now)
  const past     = rows.filter((b) => b.status === 'scheduled' && new Date(b.slot_at).getTime() <= now)
                     .concat(rows.filter((b) => b.status === 'completed' || b.status === 'no_show'))
  const cancelled = rows.filter((b) => b.status === 'cancelled')

  function onCancel(b) {
    const ok = window.confirm(
      `Cancel your appointment with Dr ${b.doctor_name} at ${formatDateTime(b.slot_at)}?\n\n` +
      `This frees the slot for another patient. You can re-book any open slot.`
    )
    if (ok) cancel.mutate({ id: b.id, reason: 'Cancelled by patient' })
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader
          title="My bookings"
          subtitle="Upcoming consultations, past visits, and anything you've cancelled."
          right={
            <Link
              to="/patient/book"
              className="text-xs font-semibold text-white bg-wantok-700 hover:bg-wantok-800 rounded px-3 py-1.5"
            >
              + Book new consultation
            </Link>
          }
        />
        {q.error && <ErrorBanner error={q.error} onRetry={() => q.refetch()} />}
        {cancel.isError && (
          <div className="mb-3 text-rose-700 bg-rose-50 border border-rose-200 rounded px-3 py-2 text-sm">
            {firstError(cancel.error, 'Could not cancel.')}
          </div>
        )}
      </Card>

      <Section title={`Upcoming (${upcoming.length})`}>
        {upcoming.length === 0 ? (
          <Empty>No upcoming consultations. Book a new one to see a doctor.</Empty>
        ) : (
          <Bookings rows={upcoming} onCancel={onCancel} cancelling={cancel.isPending} />
        )}
      </Section>

      {past.length > 0 && (
        <Section title={`Past (${past.length})`}>
          <Bookings rows={past} />
        </Section>
      )}

      {cancelled.length > 0 && (
        <Section title={`Cancelled (${cancelled.length})`}>
          <Bookings rows={cancelled} />
        </Section>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------

function Section({ title, children }) {
  return (
    <Card>
      <CardHeader title={title} />
      {children}
    </Card>
  )
}

function Empty({ children }) {
  return <div className="text-sm text-slate-400 italic">{children}</div>
}

function Bookings({ rows, onCancel, cancelling }) {
  return (
    <ul className="divide-y divide-slate-100">
      {rows.map((b) => (
        <li key={b.id} className="py-3 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-sm font-semibold text-slate-800">
              {formatDateTime(b.slot_at)}
            </div>
            <div className="text-xs text-slate-500">
              with <b>Dr {b.doctor_name || 'Unknown'}</b>
              {' · '}
              <StatusPill status={b.status} />
            </div>
            {b.reason && (
              <div className="text-xs text-slate-600 mt-1 bg-slate-50 border border-slate-200 rounded px-2 py-1">
                {b.reason}
              </div>
            )}
            {b.cancel_reason && (
              <div className="text-[11px] text-slate-500 mt-1">
                Cancel reason: {b.cancel_reason}
              </div>
            )}
          </div>
          {onCancel && b.status === 'scheduled' && (
            <button
              onClick={() => onCancel(b)}
              disabled={cancelling}
              className="shrink-0 text-xs text-rose-700 underline disabled:opacity-60"
            >
              Cancel
            </button>
          )}
        </li>
      ))}
    </ul>
  )
}

function StatusPill({ status }) {
  const tone = (
    status === 'scheduled'  ? 'bg-sky-50 border-sky-200 text-sky-800' :
    status === 'completed'  ? 'bg-emerald-50 border-emerald-200 text-emerald-800' :
    status === 'cancelled'  ? 'bg-slate-100 border-slate-300 text-slate-600' :
    status === 'no_show'    ? 'bg-rose-50 border-rose-200 text-rose-700' :
                              'bg-slate-100 border-slate-300 text-slate-600'
  )
  return (
    <span className={`inline-block uppercase text-[10px] tracking-wide border rounded-full px-2 py-0.5 ${tone}`}>
      {status}
    </span>
  )
}
